import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiFAQ } from "@/content/naati-corpus/types";
import { NAATI_FAQ_CATEGORIES, getNaatiCategoryDef } from "@/content/naati-corpus/faq-defs";

const CATEGORY_INTRO_TH: Record<string, string[]> = {
  basics: [
    "NAATI (National Accreditation Authority for Translators and Interpreters) คือหน่วยงานรับรองมาตรฐานนักแปลและล่ามของออสเตรเลีย นักแปลที่ผ่านการรับรองจะได้หมายเลข CPN (Credentialed Practitioner Number) ซึ่งใช้ตรวจสอบสถานะได้บนเว็บไซต์ NAATI โดยตรง",
    "เอกสารที่แปลโดยนักแปล NAATI Certified จะมีตราประทับ ลายเซ็น และคำรับรองความถูกต้องแนบท้าย ซึ่งเป็นมาตรฐานที่ DHA ออสเตรเลีย, Immigration NZ, IRCC แคนาดา และ UK Home Office ยอมรับโดยไม่ต้องผ่านนิติกรณ์เพิ่มเติม",
    "หมวดนี้รวมคำถามเรื่องความแตกต่างระหว่าง NAATI กับการรับรองแบบอื่น อายุของเอกสารแปล และวิธีตรวจสอบว่านักแปลมีสถานะ Certified จริง",
  ],
  "civil-registry": [
    "เอกสารทะเบียนราษฎรเป็นกลุ่มเอกสารที่ถูกขอแปล NAATI มากที่สุด ได้แก่ สูติบัตร ทะเบียนบ้าน บัตรประชาชน ทะเบียนสมรส ใบหย่า และใบเปลี่ยนชื่อ เพราะเป็นหลักฐานยืนยันตัวตนและความสัมพันธ์ในครอบครัวที่ทุกวีซ่าต้องใช้",
    "จุดที่คนมักพลาดคือการสะกดชื่อ-นามสกุลให้ตรงกับพาสปอร์ตทุกตัวอักษร และการแปลคำนำหน้า/ยศที่ไม่มีในภาษาอังกฤษ ซึ่งอาจทำให้เจ้าหน้าที่ตรวจสอบขอเอกสารเพิ่มได้",
    "หมวดนี้ครอบคลุมทั้งกรณีเอกสารสูญหาย เอกสารเก่าที่ไม่มีรูปแบบมาตรฐาน และการแปลเอกสารประกอบ เช่น ใบมรณบัตรหรือคำสั่งศาลเกี่ยวกับสิทธิปกครองบุตร",
  ],
  education: [
    "เอกสารการศึกษา เช่น ทรานสคริปต์ ใบปริญญา และใบ รบ. ต้องแปล NAATI เมื่อใช้ยื่นวีซ่านักเรียนหรือขอประเมินวุฒิการศึกษา (Skills Assessment) ในออสเตรเลียและนิวซีแลนด์",
    "สถาบันปลายทางมักต้องการให้แปลครบทุกหน้ารวมถึงตราประทับของสถาบันการศึกษา และบางกรณีต้องแปลควบคู่กับหนังสือรับรองสภาพนักศึกษาปัจจุบัน",
    "หมวดนี้ตอบคำถามเรื่องลำดับการยื่นเอกสารการศึกษาแปลควบคู่กับ Confirmation of Enrolment และการแก้ไขข้อผิดพลาดในทรานสคริปต์ที่แปลแล้ว",
  ],
  financial: [
    "เอกสารการเงินและบริษัท เช่น bank statement สลิปเงินเดือน แบบแสดงภาษี งบการเงิน และโฉนดที่ดิน มักใช้เป็นหลักฐานฐานะการเงินประกอบวีซ่าคู่ครองหรือวีซ่านักลงทุน",
    "การแปลเอกสารกลุ่มนี้ต้องรักษาตัวเลข วันที่ และชื่อบัญชีให้ตรงกับต้นฉบับเป๊ะ เพราะเจ้าหน้าที่ตรวจวีซ่ามักนำไปเทียบกับเอกสารการเงินของประเทศปลายทาง",
    "หมวดนี้รวมคำถามเรื่องการแปลเอกสารบริษัท เช่น หนังสือรับรองบริษัท บอจ.5 และสัญญาต่างๆ ที่ใช้ประกอบการยื่นวีซ่านักลงทุนหรือขยายธุรกิจ",
  ],
  "partner-visa": [
    "วีซ่าคู่ครองและคู่หมั้น (Partner/Fiancé visa) ต้องการหลักฐานความสัมพันธ์จำนวนมาก ทั้งประวัติแชท รูปถ่าย จดหมายอธิบายความสัมพันธ์ ซึ่งบางส่วนต้องแปล NAATI หากเป็นภาษาไทย",
    "เจ้าหน้าที่ DHA มักพิจารณาความสม่ำเสมอของหลักฐานตลอดระยะเวลาความสัมพันธ์ การแปลเอกสารจึงต้องคงบริบทและน้ำเสียงของข้อความต้นฉบับให้มากที่สุด",
    "หมวดนี้ตอบคำถามเกี่ยวกับวีซ่าติดตามครอบครัว วีซ่าพ่อแม่ และการเตรียมเอกสารความสัมพันธ์ให้ครบตามเช็คลิสต์ของ DHA/INZ",
  ],
  "student-work-visa": [
    "วีซ่านักเรียนและวีซ่าทักษะ (Skilled visa) มักต้องผ่านหน่วยงานประเมินทักษะวิชาชีพ เช่น VETASSESS, TRA, AHPRA หรือ ACS ซึ่งแต่ละหน่วยงานมีข้อกำหนดการแปลเอกสารต่างกัน",
    "เอกสารที่ใช้บ่อยคือใบประกอบวิชาชีพ ใบรับรองการทำงาน และหนังสือรับรองเงินเดือน ซึ่งต้องแปล NAATI ให้ตรงตามรูปแบบที่หน่วยงานประเมินกำหนด",
    "หมวดนี้ครอบคลุมทั้งขั้นตอนขอ PR ผ่านสาย Skilled Migration และคำถามเกี่ยวกับการยื่นอุทธรณ์วีซ่าที่ต้องแปลเอกสารเพิ่มเติม",
  ],
  "online-digital": [
    "บริการแปล NAATI ออนไลน์ช่วยให้ลูกค้าที่อยู่ต่างประเทศหรือต่างจังหวัดส่งไฟล์ PDF มาแปลได้โดยไม่ต้องส่งเอกสารตัวจริง โดยได้รับไฟล์แปลพร้อมตราประทับดิจิทัลกลับทางอีเมล",
    "ตราประทับดิจิทัลของนักแปล NAATI ได้รับการยอมรับใน ImmiAccount และระบบยื่นวีซ่าออนไลน์ของออสเตรเลียและนิวซีแลนด์ เทียบเท่าไฟล์สแกนจากเอกสารตราประทับจริง",
    "หมวดนี้ตอบคำถามเรื่องขนาดไฟล์ที่อัปโหลดได้ ความปลอดภัยของไฟล์เข้ารหัส และขั้นตอนส่งเอกสารต้นฉบับเพิ่มเติมหากจำเป็น",
  ],
  "pricing-turnaround": [
    "ค่าแปล NAATI คิดตามจำนวนหน้าและความซับซ้อนของเอกสาร โดยทั่วไปเอกสารทะเบียนราษฎรทั่วไปจะมีราคาค่อนข้างคงที่ ส่วนเอกสารกฎหมาย/การเงินที่ซับซ้อนจะคิดตามปริมาณคำ",
    "งานด่วนภายใน 1 วันหรือบริการนอกเวลาทำการอาจมีค่าธรรมเนียมเพิ่มเติม ลูกค้าควรแจ้งกำหนดยื่นวีซ่าล่วงหน้าเพื่อวางแผนคิวแปลให้ทัน",
    "หมวดนี้ตอบคำถามเรื่องราคาต่อหน้า ส่วนลดงานหลายฉบับ และระยะเวลาทำงานมาตรฐานเทียบกับงานเร่งด่วน",
  ],
  "coverage-area": [
    "ทีมนักแปล NAATI ให้บริการครอบคลุมทั่วประเทศไทย ทั้งกรุงเทพฯ ภูเก็ต เชียงใหม่ พัทยา และภาคอีสาน เช่น ขอนแก่น อุดรธานี และหนองคาย ซึ่งเป็นพื้นที่ที่มีความต้องการแปลเอกสารยื่นวีซ่าออสเตรเลียสูง",
    "ลูกค้าที่อยู่ต่างจังหวัดสามารถส่งเอกสารทางไปรษณีย์หรือ EMS และรับเอกสารแปลกลับทางเดียวกัน หรือเลือกใช้บริการแปลออนไลน์เพื่อความรวดเร็ว",
    "หมวดนี้รวมคำถามเฉพาะพื้นที่ เช่น การหาศูนย์แปล NAATI ใกล้บ้าน และการส่งเอกสารข้ามจังหวัดโดยไม่ต้องเดินทางมากรุงเทพฯ",
  ],
  "other-countries": [
    "แม้ NAATI จะเป็นมาตรฐานของออสเตรเลีย แต่หน่วยงานตรวจคนเข้าเมืองของนิวซีแลนด์ (Immigration NZ / INZ) ก็ยอมรับคำแปลจากนักแปล NAATI Certified เช่นกัน",
    "สำหรับแคนาดา (IRCC) และสหราชอาณาจักร (UK Home Office) การแปล NAATI มักได้รับการยอมรับในฐานะคำแปลรับรองมาตรฐานสากล แต่ควรตรวจสอบข้อกำหนดล่าสุดของแต่ละหน่วยงานก่อนยื่น",
    "หมวดนี้ตอบคำถามเปรียบเทียบข้อกำหนดของแต่ละประเทศ และกรณีที่ต้องแปลเอกสารชุดเดียวกันเพื่อยื่นหลายประเทศพร้อมกัน",
  ],
};

export const Route = createFileRoute("/naati/faq/$category")({
  loader: async ({ params }) => {
    const { getNaatiFaqsByCategoryTh } = await import("@/content/naati-corpus/categories-th");
    const { NAATI_KEYWORDS_TH } = await import("@/content/naati-corpus/keywords-th");
    const def = getNaatiCategoryDef(params.category);
    if (!def) throw notFound();
    const faqs = getNaatiFaqsByCategoryTh(def.slug);
    const keywords = NAATI_KEYWORDS_TH.filter((k) => def.matchTh.test(k)).slice(0, 40);
    return { def, faqs, keywords };
  },
  component: NaatiFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "หมวดคำถามไม่พบ" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleTh} — NAATI แปลรับรอง FAQ ${faqs.length} ข้อ (2026)`,
        description: def.descTh,
        path: `/naati/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/naati/faq/${params.category}` }],
        keywords: loaderData.keywords,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "NAATI Translation", url: `${site}/naati` },
              { name: "NAATI FAQ", url: `${site}/naati/faq` },
              { name: def.titleTh, url: `${site}/naati/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/naati/faq/${params.category}`)) },
      ],
    };
  },
});

function NaatiFaqCategoryPage() {
  const { def, faqs, keywords } = Route.useLoaderData();
  const intro = CATEGORY_INTRO_TH[def.slug] ?? [];
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/naati" className="hover:underline">NAATI Translation</a> <span>/</span>{" "}
          <a href="/naati/faq" className="hover:underline">NAATI FAQ</a> <span>/</span>{" "}
          <span>{def.titleTh}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleTh}</h1>
        <p className="text-muted-foreground mb-6">{def.descTh}</p>

        <div className="mb-8 space-y-3 text-sm leading-relaxed text-muted-foreground">
          {intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {keywords.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2 text-xs">
            {keywords.map((kw: string, i: number) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        )}

        <p className="text-sm text-muted-foreground mb-8">
          รวม <strong>{faqs.length}</strong> คำถามในหมวดนี้ — ตอบโดยทีมแปล NAATI ที่ได้รับ CPN อัปเดต 2026
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {NAATI_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/naati/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleTh}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: NaatiFAQ, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              ยังไม่มีคำถามในหมวดนี้ — <a href="/contact" className="text-primary underline">สอบถามทีมงาน</a>
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ให้ทีมนักแปล NAATI ดูแลเอกสารครบวงจร</h2>
          <p className="text-sm text-muted-foreground mb-4">
            แปลรับรอง NAATI → ตรวจทาน → ส่งไฟล์ PDF/EMS → ยื่น DHA / INZ / IRCC
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ขอใบเสนอราคา →
          </a>
        </section>

        <PageGallery category="naati" seed="naati-faq-category" locale="th" />
        <FaqSourceNote topic="translation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
